import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FeedTypeEnum, TagEnum } from '@orijinworks/frontend-commons';
import { getAllFeeds, getFeedsWithMessages, joinFeedRoom } from '../../services/Feed.service';
import { transformApiFeedToDisplay, transformSocketEventToFeed, removeTagTypeFromTags } from '../../mappers/feedUtility';
import { PAGINATION_CONSTANTS, NAVIGATION_EVENTS } from '../../constants';

/**
 * Hook for managing All Feeds display functionality
 * Handles feed listing, pagination, socket events for new feeds and messages
 * Clean separation with minimal props from parent component
 * Only loads data when the All Feeds tab is active
 * Gets user data directly from localStorage
 */
export const useAllFeedsHook = (onNavigateToMessages, isActive = true) => {
  // Core state
  
  // Get user details from Redux store
  const {userDetail} = useSelector((state) => state.app || {});
  const currentUserKey = userDetail?.userId;  
  const [feeds, setFeeds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalFeedCount, setTotalFeedCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION_CONSTANTS.DEFAULT_FEED_SIZE);
  const [hasJoinedSocketRooms, setHasJoinedSocketRooms] = useState(false);
  const [hasLoadedInitialFeeds, setHasLoadedInitialFeeds] = useState(false);
  const [hasMore, setHasMore] = useState(true);


  /**
   * Mark a feed as read (remove new tag, reset unread count)
   */
  const markFeedAsRead = useCallback((feedId) => {
    setFeeds((prevFeeds) =>
      prevFeeds.map((feed) => {
        if (feed.id === feedId) {
          return {
            ...feed,
            isNew: false,
            unreadCount: 0,
            tags: removeTagTypeFromTags(feed.tags, TagEnum.New),
          };
        }
        return feed;
      })
    );
  }, []);

  /**
   * Scroll to a specific feed element
   */
  const scrollToFeed = useCallback((feedId) => {
    setTimeout(() => {
      const element = document.querySelector(`#${feedId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, []);

  /**
   * Hide a feed from the list
   */
  const handleFeedHide = useCallback((feedId) => {
    setFeeds((prevFeeds) => prevFeeds.filter((feed) => feed.id !== feedId));
  }, []);

  /**
   * Handle clicking on a feed item
   */
  const handleFeedClick = useCallback(
    (feed) => {
      // Mark as read by removing new tag
      markFeedAsRead(feed.id);

      // Navigate based on feed type
      if (feed.type === FeedTypeEnum.dm || feed.type === FeedTypeEnum.group) {
        // DM type
        if (onNavigateToMessages) {
          onNavigateToMessages(NAVIGATION_EVENTS.NAVIGATE_TO_DM, feed);
        }
      } else {
        // For announcements/channels, scroll to the feed
        scrollToFeed(feed.id);
      }
    },
    [onNavigateToMessages, markFeedAsRead, scrollToFeed]
  );

  /**
   * Join socket rooms for all feeds to receive real-time updates
   */
  const joinAllSocketRooms = useCallback(async () => {
    try {
      const allFeeds = await getAllFeeds();
      if (allFeeds.length > 0) {
        const feedRoomIds = allFeeds.map((feed) => `feed_${feed.id}`);
        joinFeedRoom(feedRoomIds);
        setHasJoinedSocketRooms(true);
      }
    } catch (error) {
      console.error('[AllFeeds] Failed to join socket rooms:', error);
    }
  }, []);

  /**
   * Load paginated feeds for display
   */
  const loadFeedsPage = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getFeedsWithMessages(currentPage, pageSize);

      if (response.data && response.data.length > 0) {
        const transformedFeeds = response.data.map((feedData) => {
          const displayFeed = transformApiFeedToDisplay(feedData, currentUserKey, 'all');
          return {
            ...displayFeed,
            onViewFullHandler: handleFeedClick,
            onHideHandler: handleFeedHide,
          };
        });

        setFeeds(transformedFeeds);
      } else {
        setFeeds([]);
      }

      setTotalFeedCount(response.totalFeedCount || 0);
      setHasLoadedInitialFeeds(true);

      // Update hasMore state based on response.hasMore
      setHasMore(response.hasMore || false);
    } catch (error) {
      console.error('[AllFeeds] Failed to load feeds:', error);
      setFeeds([]);
      setTotalFeedCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, currentUserKey, handleFeedClick, handleFeedHide]);

  /**
   * Handle when a new feed is created via socket
   */
  const handleFeedCreated = useCallback((socketData) => {
    if (socketData.feed_id) {
      // Join the new feed's socket room
      joinFeedRoom([`feed_${socketData.feed_id}`]);
    }
    // ✅ Removed redundant cross-tab notification - Messages hook handles its own socket events
  }, []);

  /**
   * Handle new message received via socket
   */
  const handleNewMessageReceived = useCallback((socketData) => {
    // Don't show own messages in All Feeds
    if (socketData.sender_id === currentUserKey) {
      return;
    }

    // Transform socket data to display format
    const displayFeed = transformSocketEventToFeed(socketData, currentUserKey, 'all');

    // Add to feeds list at the top with handlers
    const newFeed = {
      ...displayFeed,
      onViewFullHandler: handleFeedClick,
      onHideHandler: handleFeedHide,
    };

    setFeeds((prevFeeds) => [newFeed, ...prevFeeds]);
    setTotalFeedCount((prev) => prev + 1);
  }, [currentUserKey, handleFeedClick, handleFeedHide]);

  // Socket event handlers are now called from useFeedsManager
  // No need for individual socket listeners here

  // Join socket rooms once when hook initializes
  useEffect(() => {
    if (!hasJoinedSocketRooms) {
      joinAllSocketRooms();
    }
  }, [hasJoinedSocketRooms, joinAllSocketRooms]);

  // Load feeds only when All Feeds tab becomes active and hasn't been loaded yet
  useEffect(() => {
    if (isActive && !hasLoadedInitialFeeds) {
      loadFeedsPage();
    }
  }, [isActive, hasLoadedInitialFeeds, loadFeedsPage]);

  // Handle pagination changes (deps exclude loadFeedsPage / hasLoadedInitialFeeds to avoid a second fetch when initial load flips hasLoadedInitialFeeds)
  useEffect(() => {
    if (isActive && hasLoadedInitialFeeds) {
      loadFeedsPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only on page/size; full deps would re-run when hasLoadedInitialFeeds becomes true (duplicate with effect above)
  }, [currentPage, pageSize]);

  /**
   * Handle page change for pagination
   */
  const handlePageChange = useCallback((event, newPage) => {
    setCurrentPage(newPage);
  }, []);

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((event) => {
    const newSize = event.target.value;
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
  }, []);


  return {
    // State
    feeds,
    isLoading,
    totalFeedCount,
    currentPage,
    pageSize,
    hasMore,
    // Actions
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onFeedClick: handleFeedClick,
    refreshFeeds: loadFeedsPage,

    // Socket event handlers (called from useFeedsManager)
    onFeedCreated: handleFeedCreated,
    onMessageReceived: handleNewMessageReceived,
  };
};
